import { useAppConfig } from '@/features/acl/acl-provider';
import { useLocale } from '@/lib/hooks/use-locale';
import type { ExplorerGetForNormalisedCategoryItem } from '@/server/types/infer-trpc-types';
import type {
    BillingStatus,
    CustomDateRange,
    ExplorerSort,
    ExplorerViewFilters,
    ExplorerViewHandles,
    TargetUnit,
    VisibilityWindow,
} from '@/server/utils/explorer-utils';
import { safeParseBreadcrumbs } from '@/server/utils/insights-utils';
import { api } from '@/utils/api';
import { forwardRef, useMemo } from 'react';
import { useConfigurableExplorerColumns } from '../hooks/use-configurable-explorer-columns';
import { useExplorerSorting } from '../hooks/use-explorer-sorting';
import useRowSelection from '../hooks/use-manage-explorer-filters';
import { usePreventExplorerUnnecissearyRerender } from '../hooks/use-prevent-explorer-unnecisseary-rerender';
import { ExplorerDataTable } from './explorer-data-table';

type Props = {
    hideUnselectedRows?: boolean;
    searchString?: string;
    window: VisibilityWindow;
    customDateRange: CustomDateRange | null;
    filters: ExplorerViewFilters;
    targetUnit: TargetUnit;
    billingStatus: BillingStatus;
    onFiltersChange: (filters: string[]) => void;
    sortingFilter: ExplorerSort | null;
    setSortingFilter: (sortingFilter: ExplorerSort | null) => void;
    yearPeriods: number;
};

export const ExplorerViewNormalisedCategory = forwardRef<ExplorerViewHandles, Props>(
    (
        {
            hideUnselectedRows = false,
            searchString,
            window,
            customDateRange,
            filters,
            targetUnit,
            billingStatus,
            onFiltersChange,
            sortingFilter,
            setSortingFilter,
            yearPeriods,
        },
        ref,
    ) => {
        const { t } = useLocale();
        const { breadcrumbDepthsToHide, normalisedCategoryDepthsUsedInInsightsInExplorer } = useAppConfig();
        const normalisedCategoryDepth = normalisedCategoryDepthsUsedInInsightsInExplorer!;

        const { sorting, sortChangedInInterface } = useExplorerSorting(sortingFilter, setSortingFilter);

        const { filtersWithRerenderProtection } = usePreventExplorerUnnecissearyRerender(
            filters,
            'normalisedCategory',
            hideUnselectedRows,
        );

        const {
            data: dataRaw,
            isLoading,
            isFetching,
            isFetchingNextPage,
            hasNextPage,
            fetchNextPage,
        } = api.platoUser.query.explorer.getForNormalisedCategory.getListWithFilters.useInfiniteQuery(
            {
                searchString,
                hideUnselectedRows,
                filters: filtersWithRerenderProtection,
                customDateRange,
                billingStatus,
                window,
                sorting: sortingFilter,
                yearPeriods,
                normalisedCategoryDepth,
            },
            {
                getNextPageParam: (lastPage) => lastPage.nextCursor,
            },
        );

        const columns = useConfigurableExplorerColumns({
            columns: [
                {
                    id: 'name',
                    accessorKey: 'name',
                    headerText: t('header_tab__NORMALISED_CATEGORY', {
                        ns: 'explorer',
                    }),
                    generateCell: (row) => {
                        // Type assertion: we know our data is ExplorerGetForNormalisedCategoryItem which includes breadcrumbs
                        const typedRow = row as ExplorerGetForNormalisedCategoryItem;
                        const breadcrumbs = safeParseBreadcrumbs(typedRow.breadcrumbs);
                        const filteredBreadcrumbs = breadcrumbs?.slice(breadcrumbDepthsToHide) || [];

                        // Get the parent category (last breadcrumb) and current category
                        const parentCategory =
                            filteredBreadcrumbs.length > 0
                                ? filteredBreadcrumbs[filteredBreadcrumbs.length - 1]?.title
                                : '';
                        const categoryName = typedRow.name;

                        // Format as "parentCategoryName / categoryName"
                        const displayText = parentCategory
                            ? `${parentCategory} / ${categoryName}`
                            : categoryName;

                        return displayText;
                    },
                    size: 250,
                },
            ],
        });

        const data = useMemo(() => {
            return dataRaw ? dataRaw.pages.flatMap((page) => page.result) : [];
        }, [dataRaw]);

        const { rowSelection, setRowSelection } = useRowSelection<ExplorerGetForNormalisedCategoryItem, 'id'>(
            data,
            'id',
            onFiltersChange,
            filters.normalisedCategory,
        );

        return (
            <ExplorerDataTable
                ref={ref}
                data={data}
                columns={columns}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetchingNextPage}
                isFetching={isFetching}
                hasNextPage={hasNextPage}
                isLoading={isLoading}
                filters={filters}
                window={window}
                billingStatus={billingStatus}
                customDateRange={customDateRange}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                sorting={sorting}
                sortChangedInInterface={sortChangedInInterface}
            />
        );
    },
);
ExplorerViewNormalisedCategory.displayName = 'ExplorerViewNormalisedCategory';
